import React from "react";
import {
  SafeAreaView,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { CreateRoomViewProps } from "../createRoom.types";
import SharedCareCard from "./SharedCareCard";
import DateTimePicker from "./DateTimePicker";
import { canProceedCreateRoom } from "../canProceed";
import { TOPICS } from "../../../constants/topics";
import TopicCard from "./TopicCars";
import {
  BETA_TIME_SLOT_NOTICE,
  sharedCareRules,
  styles,
  PREDEFINED_MEETING_NAMES,
} from "./CreateRoomView.shared";
import useStepScrollToTop from "../../../hooks/useStepScrollToTop";
import Dropdown from "../../../common/Dropdown";

export default function CreateRoomViewDesktop({
  currentStep,
  onStepChange,
  onAgreeToTerms,
  selectedHour,
  selectedMinute,
  selectedAmPm,
  selectedDate,
  roomName,
  roomNameError,
  currentTimezone,
  onChangeHour,
  onChangeMinute,
  onChangeAmPm,
  onChangeDate,
  onChangeRoomName,
  selectedTopicIds,
  onToggleTopic,
  isSubmitting,
  onSubmit,
  onCancel,
  onBack,
  selectedMeetingNameOption,
  onChangeSelectedMeetingNameOption,
  maxParticipants,
  onChangeMaxParticipants,
}: CreateRoomViewProps) {
  const scrollRef = useStepScrollToTop(currentStep);

  const renderStepIndicator = () => {
    const renderStep = (stepNumber: 1 | 2 | 3, label: string) => {
      const isActive = currentStep === stepNumber;
      const isCompleted = currentStep > stepNumber;
      return (
        <View style={styles.stepContainer}>
          <View
            style={[
              styles.stepCircle,
              (isActive || isCompleted) && styles.stepCircleActive,
            ]}
          >
            <Text
              style={[
                styles.stepNumber,
                (isActive || isCompleted) && styles.stepNumberActive,
              ]}
            >
              {stepNumber}
            </Text>
          </View>
          <Text
            style={[
              styles.stepLabel,
              isActive && styles.stepLabelActive,
              isCompleted && styles.stepLabelCompleted,
            ]}
          >
            {label}
          </Text>
        </View>
      );
    };

    return (
      <View style={styles.stepIndicator} pointerEvents="none">
        {renderStep(1, "SHARED CARE AGREEMENT")}
        {renderStep(2, "BASIC INFORMATION")}
        {renderStep(3, "TOPICS")}
      </View>
    );
  };

  const renderStep1 = () => {
    return (
      <View style={styles.stepContent}>
        <View style={styles.gridContainer}>
          {sharedCareRules.map((rule, index) => (
            <SharedCareCard
              key={index}
              title={rule.title}
              description={rule.description}
              icon={rule.icon}
              expandedContent={rule.expandedContent}
            />
          ))}
        </View>
      </View>
    );
  };

  const renderStep2 = () => {
    return (
      <View style={styles.stepContent}>
        <View style={styles.formCard}>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>CONVERSATION NAME</Text>
            <Dropdown
              label="Select Predefined Name or Make Your Own"
              value={selectedMeetingNameOption}
              options={PREDEFINED_MEETING_NAMES}
              onChange={onChangeSelectedMeetingNameOption}
            />
          </View>

          {selectedMeetingNameOption === "Custom / Make your own..." && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>CUSTOM CONVERSATION NAME</Text>
              <TextInput
                value={roomName}
                onChangeText={onChangeRoomName}
                placeholder="Let's talk about hobbies, Dreams, Healthy lifestyle"
                placeholderTextColor="#b1afaf"
                style={[styles.input, roomNameError && styles.inputError]}
              />
              {roomNameError && (
                <Text style={styles.errorText}>{roomNameError}</Text>
              )}
            </View>
          )}

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>MAX PARTICIPANTS</Text>
            <Dropdown
              label="Select capacity (up to 4)"
              value={`${maxParticipants} participants`}
              options={["2 participants", "3 participants", "4 participants"]}
              onChange={(val) => {
                const count = parseInt(val.split(" ")[0], 10);
                onChangeMaxParticipants(count);
              }}
            />
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>PICK YOUR TIME</Text>
            <Text style={styles.betaNotice}>{BETA_TIME_SLOT_NOTICE}</Text>
            <DateTimePicker
              selectedDate={selectedDate}
              selectedHour={selectedHour}
              selectedMinute={selectedMinute}
              selectedAmPm={selectedAmPm}
              onDateChange={onChangeDate}
              onHourChange={onChangeHour}
              onMinuteChange={onChangeMinute}
              onAmPmChange={onChangeAmPm}
            />
            <Text style={styles.helperText}>
              Time will be displayed in each participant&#39;s local timezone.
              Your timezone: {currentTimezone}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderStep3 = () => {
    return (
      <View style={styles.stepContent}>
        <View style={styles.topicsGridContainer}>
          {TOPICS.map((topic) => (
            <TopicCard
              key={topic.id}
              title={topic.label}
              description={topic.description}
              selected={selectedTopicIds.includes(topic.id)}
              onToggle={() => onToggleTopic(topic.id)}
            />
          ))}
        </View>
      </View>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return null;
    }
  };

  const canProceed = () =>
    canProceedCreateRoom(currentStep, {
      selectedMeetingNameOption,
      roomName,
      selectedHour,
      selectedMinute,
      selectedAmPm,
      selectedTopicIds,
    });

  const handleNext = () => {
    if (currentStep < 3) {
      onStepChange((currentStep + 1) as 1 | 2 | 3);
    } else {
      onSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      onStepChange((currentStep - 1) as 1 | 2 | 3);
    } else {
      onBack();
    }
  };

  return (
    <SafeAreaView style={styles.root}>
      <ScrollView
        ref={scrollRef}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.content, styles.contentDesktop]}>
          <View style={styles.mainContent}>
            <Text style={styles.pageTitle}>Create a New Conversation</Text>

            {renderStepIndicator()}

            {renderCurrentStep()}
          </View>

          <View style={styles.footerSection}>
            <View style={styles.footer}>
              <TouchableOpacity
                onPress={currentStep === 1 ? onCancel : handleBack}
                style={styles.cancelButton}
              >
                <Text style={styles.cancelButtonText}>
                  {currentStep === 1 ? "CANCEL" : "BACK"}
                </Text>
              </TouchableOpacity>
              {currentStep === 1 ? (
                <TouchableOpacity
                  onPress={() => {
                    onAgreeToTerms();
                    onStepChange(2);
                  }}
                  style={styles.continueButton}
                >
                  <Text style={styles.continueButtonText}>
                    AGREE AND CONTINUE
                  </Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  onPress={handleNext}
                  style={[
                    styles.continueButton,
                    (!canProceed() || isSubmitting) &&
                      styles.continueButtonDisabled,
                  ]}
                  disabled={!canProceed() || isSubmitting}
                >
                  <Text style={styles.continueButtonText}>
                    {currentStep === 3
                      ? isSubmitting
                        ? "CREATING..."
                        : "CONFIRM"
                      : "CONTINUE"}
                  </Text>
                </TouchableOpacity>
              )}
            </View>

            <View style={styles.bottomFooter}>
              <View style={styles.footerSpacer} />
              <Text style={styles.copyright}>(c) Copyright KNEXO 2025</Text>
              <View style={styles.feedbackWrapper}>
                <TouchableOpacity style={styles.feedbackButton}>
                  <Ionicons
                    name="chatbubble-outline"
                    size={14}
                    color="#f6f7f9"
                  />
                  <Text style={styles.feedbackText}>FEEDBACK</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
